import PermissionButton from '../../permissions/PermissionButton';
import React, { useState } from 'react';
import { Button, message, Modal } from 'antd';
import { PrinterOutlined, FilePdfOutlined } from '@ant-design/icons';
import html2pdf from 'html2pdf.js';

const formatDate = (date) => {
  if (!date) return "Non fourni";
  
  const dateObj = new Date(date);
  
  // Format date in European style (dd/MM/yyyy)
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  
  return `${day}/${month}/${year}`;
};

// Inline styles for printing
const styles = `
  <style>
    .print-groupe-container {
      font-family: Arial, sans-serif;
      color: #000;
      max-width: 210mm;
      margin: 0 auto;
      padding: 5mm 8mm 10mm 8mm;
      box-sizing: border-box;
    }
    
    .print-header {
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid #ccc;
      padding-bottom: 8px;
      margin-bottom: 12px;
    }
    
    .print-header-left h2 {
      margin: 0 0 5px 0;
      color: #1890ff;
      font-size: 18px;
    }
    
    .print-header-left p, .print-header-right p {
      margin: 3px 0;
      font-size: 14px;
    }
    
    .print-items {
      margin-bottom: 20px;
    }
    
    .items-table {
      width: 100%;
      border-collapse: collapse;
    }
    
    .items-table th {
      background-color: #f0f0f0;
      text-align: left;
      padding: 8px;
      font-size: 13px;
      border-bottom: 1px solid #ccc;
    }
    
    .items-table td {
      padding: 6px 8px;
      font-size: 12px;
      border-bottom: 1px solid #eee;
    }
    
    .totals-table {
      margin-top: 20px;
      width: 50%;
      margin-left: auto;
      border-collapse: collapse;
    }
    
    .totals-table td {
      padding: 6px 8px;
      font-size: 13px;
    }
    
    .total-value {
      text-align: right;
      font-weight: bold;
    }
    
    .page-break {
      page-break-after: always;
    }
    
    .table-footer {
      font-size: 12px;
      text-align: right;
      padding: 5px 0;
      font-style: italic;
    }
    
    .print-footer {
      margin-top: 30px;
      border-top: 1px solid #eee;
      padding-top: 10px;
      display: flex;
      justify-content: space-between;
      font-size: 11px;
      color: #666;
    }
    
    .small-text {
      font-size: 10px;
    }
    
    .totals-summary {
      margin-top: 20px;
      text-align: right;
      padding: 10px;
      background-color: #f8f8f8;
      border-top: 1px solid #ddd;
      border-bottom: 1px solid #ddd;
    }
    
    .total-line {
      font-size: 14px;
      font-weight: bold;
      margin: 0;
    }
  </style>
`;

export const PrintGroupe = ({ bonliv, items }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    try {
      if (!bonliv || !items) {
        message.error('Données insuffisantes pour l\'impression');
        return;
      }
      
      setLoading(true);
      
      // Create print content
      const printContent = document.createElement('div');
      printContent.className = 'print-groupe-container';
      printContent.innerHTML = styles + generatePrintContent(bonliv, items);
      
      // Temporarily add to document
      document.body.appendChild(printContent);
      
      // Generate PDF
      const opt = {
        margin: [5, 5, 5, 5], // [top, right, bottom, left] margins in mm
        filename: `BL_${bonliv.REF_BL}_Groupe.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      // Instead of saving, get the blob
      const pdf = await html2pdf().set(opt).from(printContent).output('blob');
      
      // Create a blob URL
      const url = URL.createObjectURL(pdf);
      setPdfUrl(url);
      
      // Clean up DOM
      document.body.removeChild(printContent);
      setLoading(false);
      
      // Show modal with PDF
      setIsModalVisible(true);
    } catch (error) {
      setLoading(false);
      message.error(`Échec de l'impression: ${error.message}`);
      console.error('Erreur d\'impression:', error);
    }
  };

  const handleDirectPrint = () => {
    if (pdfUrl) {
      // Open in new window and print
      const printWindow = window.open(pdfUrl, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };
  
  const handleModalClose = () => {
    setIsModalVisible(false);
    // Revoke blob URL to free memory
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };
  
  return (
    <>
      <PermissionButton permissionModule="deliveryNotes" permissionAction="print"
        onClick={handlePrint}
        icon={<FilePdfOutlined />}
        type="primary"
        loading={loading}
        data-print-groupe-button="true"
      >
        Imprimer Groupe
      </PermissionButton>
      
      <Modal
        title={`Bon de Livraison - ${bonliv?.REF_BL || ''}`}
        open={isModalVisible}
        onCancel={handleModalClose}
        width={1000}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Fermer
          </Button>,
          <PermissionButton permissionModule="deliveryNotes" permissionAction="print"
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />} 
            onClick={handleDirectPrint}
          >
            Imprimer
          </PermissionButton>
        ]}
      >
        {pdfUrl && (
          <div style={{ height: '70vh', width: '100%', overflow: 'auto' }}>
            <iframe 
              src={pdfUrl} 
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Bon de Livraison Preview"
            />
          </div>
        )}
      </Modal>
    </>
  );
};

// Generate the HTML content for printing
const generatePrintContent = (bonliv, items) => {
  // Calculate number of unique articles
  const uniqueArticles = new Set(items.map(item => item.CODE_ART)).size;
  
  // Sort items by CODE_ART
  const sortedItems = [...items].sort((a, b) => {
    if (a.CODE_ART < b.CODE_ART) return -1;
    if (a.CODE_ART > b.CODE_ART) return 1;
    return 0;
  });
  
  // Filter items to only include those with qte_liv > 0
  const filteredItems = sortedItems.filter(item => {
    // Default to QTE if qte_liv doesn't exist
    const qteLiv = item.qte_liv !== undefined ? item.qte_liv : item.QTE;
    return qteLiv > 0 && qteLiv !== -1;
  });
  
  // Split items into chunks of 27 for pagination
  const chunks = [];
  const itemsPerPage = 33;
  
  for (let i = 0; i < filteredItems.length; i += itemsPerPage) {
    chunks.push(filteredItems.slice(i, i + itemsPerPage));
  }
  
  // If no items, create an empty chunk
  if (chunks.length === 0) {
    chunks.push([]);
  }
  
  let content = '';
  
  // Generate content for each page
  chunks.forEach((chunk, index) => {
    const isLastPage = index === chunks.length - 1;
    
    content += `
      <div ${!isLastPage ? 'class="page-break"' : ''}>
        ${index === 0 ? `
          <div class="print-header">
            <div class="print-header-left">
              <h2>Bon de Livraison: ${bonliv.REF_BL}</h2>
              <p>Date: ${formatDate(bonliv.DATE_BL)}</p>
            </div>
            <div class="print-header-right">
              <p>Client: ${bonliv.CLIENT}</p>
              <p>Compte: ${bonliv.COMPTE || "N/A"}</p>
            </div>
          </div>
        ` : `
          <div class="print-header">
            <div class="print-header-left">
              <h2>Bon de Livraison: ${bonliv.REF_BL} (suite)</h2>
            </div>
            <div class="print-header-right">
              <p>Page ${index + 1}/${chunks.length}</p>
            </div>
          </div>
        `}
        
        <div class="print-items">
          <table class="items-table">
            <thead>
              <tr>
                <th>Code Art.</th>
                <th>Article</th>
                <th>Qté</th>
                
              </tr>
            </thead>
            <tbody>
              ${generateItemsRows(bonliv, chunk)}
            </tbody>
            ${!isLastPage ? `
              <tfoot>
                <tr>
                  <td colspan="6" class="table-footer">Page ${index + 1} - Suite au verso</td>
                </tr>
              </tfoot>
            ` : ''}
          </table>
        </div>
        
        ${isLastPage ? `
          <div class="totals-summary">
            <p class="total-line">Nombre d'articles: ${uniqueArticles}</p>
          </div>
          
          <div class="print-footer">
            <p>Page ${index + 1}/${chunks.length}</p>
            <p class="small-text">Document imprimé le ${formatDate(new Date())}</p>
          </div>
        ` : ''}
      </div>
    `;
  });
  
  return content;
};

// Generate rows for each item
const generateItemsRows = (bonliv, items) => {
  if (!items || items.length === 0) return '<tr><td colspan="6">Aucun article</td></tr>';
  
  return items.map(item => {
    // Always use qte_liv for quantities
    const quantity = item.qte_liv;
    const totalTTC = Math.round((quantity * (1 - item.REMISE / 100) * item.PV_TTC) * 100) / 100;
    
    return `
      <tr>
        <td>${item.CODE_ART}</td>
        <td>${item.ARTICLE}</td>
        <td>${quantity}</td>
        
      </tr>
    `;
  }).join('');
};

export default PrintGroupe; 